import { ModalForm, ProFormSelect, ProFormText } from '@ant-design/pro-components';
import { Form, Switch, TreeSelect } from 'antd';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import {
  getOperationEntityParentOptions,
  isRootOperationEntity,
} from '../utils/operation-entities';
import {
  buildPrincipalCandidateTree,
  getPrincipalContactTreesForEntity,
  getPrincipalCandidateIds,
  reconcilePrincipalCandidateIds,
  splitPrincipalCandidateIds,
} from '../utils/operation-entity-principals';

const getInitialValues = (entity) => {
  if (!entity) {
    return { enabled: true, memberIds: [] };
  }
  return {
    ...entity,
    principalCandidateIds: getPrincipalCandidateIds(entity.principals),
  };
};

const OperationEntityForm = ({
  entities,
  entity,
  memberOptions,
  onFinish,
  onOpenChange,
  open,
  principalContext,
}) => {
  const [form] = Form.useForm();
  const isRoot = entity && isRootOperationEntity(entity);
  const selectedParentId = Form.useWatch('parentId', form) || entity?.parentId;
  const watchedEntityName = Form.useWatch('name', form);
  const selectedEntityName = watchedEntityName === undefined ? entity?.name : watchedEntityName;
  const selectedParent = entities.find((candidate) => candidate.id === selectedParentId);
  const parentOptions = getOperationEntityParentOptions(entities, entity?.id);
  const parentTreeData = parentOptions.map(({ label, value }) => ({ title: label, value }));
  const currentPrincipalOptions = (entity?.principals || []).map((principal) => ({
    label: principal.name || principal.phoneNumber || principal.id,
    value: principal.memberId || principal.id,
  }));
  const hasEditableEntityName = entity && !isRoot && !entity.isStore;
  const contactScope = {
    entityName: hasEditableEntityName ? selectedEntityName : undefined,
    isRoot,
    parentName: selectedParent?.name,
  };
  const contactScopeKey = `${contactScope.parentName || ''}\n${contactScope.entityName || ''}`;
  const previousContactScopeKey = useRef(contactScopeKey);
  const { contactTree, groupContactTree } = getPrincipalContactTreesForEntity(
    principalContext?.contactTree,
    contactScope,
  );
  const principalTreeData = buildPrincipalCandidateTree({
    contactTree,
    groupContactTree,
    memberOptions: currentPrincipalOptions,
    recommendedExternalUserId: principalContext?.recommendedContact?.externalUserId,
  });
  const principalTreeDataRef = useRef(principalTreeData);
  principalTreeDataRef.current = principalTreeData;
  useEffect(() => {
    if (previousContactScopeKey.current !== contactScopeKey) {
      previousContactScopeKey.current = contactScopeKey;
      const selectedIds = form.getFieldValue('principalCandidateIds') || [];
      form.setFieldValue(
        'principalCandidateIds',
        reconcilePrincipalCandidateIds(selectedIds, principalTreeDataRef.current),
      );
    }
  }, [contactScopeKey, form]);
  let title = '新建区域';
  if (entity) {
    title = isRoot ? '维护根实体负责人' : `编辑经营实体：${entity.name}`;
  }

  return (
    <ModalForm
      key={entity?.id || 'create'}
      title={title}
      form={form}
      open={open}
      initialValues={getInitialValues(entity)}
      modalProps={{ destroyOnHidden: true }}
      onFinish={(values) => {
        const principalIds = entity
          ? splitPrincipalCandidateIds(values.principalCandidateIds)
          : { memberIds: values.memberIds };
        return onFinish({
          ...values,
          ...principalIds,
          ...(entity
            ? {
                collectionId: principalContext?.collectionId,
                enabled: isRoot ? entity.enabled : values.enabled,
                id: entity.id,
                name: entity.isStore || isRoot ? entity.name : values.name,
                parentId: isRoot ? entity.parentId : values.parentId,
              }
            : {}),
        });
      }}
      onOpenChange={onOpenChange}
    >
      {entity ? (
        <>
          <ProFormText name="code" label="实体编码" disabled />
          {!isRoot ? (
            <Form.Item
              name="parentId"
              label="上级实体"
              rules={[{ required: true, message: '请选择上级实体' }]}
            >
              <TreeSelect
                showSearch={{ treeNodeFilterProp: 'title' }}
                treeData={parentTreeData}
                treeDefaultExpandAll
              />
            </Form.Item>
          ) : null}
          {!entity.isStore && !isRoot ? (
            <ProFormText
              name="name"
              label="实体名称"
              rules={[{ required: true, message: '请输入实体名称' }]}
            />
          ) : null}
          {!isRoot ? (
            <Form.Item
              name="enabled"
              label="启用"
              valuePropName="checked"
              extra="停用后不纳入经营分析，包括历史期间；闭店不等于停用。"
            >
              <Switch checkedChildren="启用" unCheckedChildren="停用" />
            </Form.Item>
          ) : null}
        </>
      ) : (
        <>
          <ProFormText
            name="code"
            label="实体编码"
            rules={[{ required: true, message: '请输入实体编码' }]}
          />
          <ProFormText
            name="name"
            label="实体名称"
            rules={[{ required: true, message: '请输入实体名称' }]}
          />
          <Form.Item
            name="parentId"
            label="上级实体"
            rules={[{ required: true, message: '请选择上级实体' }]}
          >
            <TreeSelect
              showSearch={{ treeNodeFilterProp: 'title' }}
              treeData={parentTreeData}
              treeDefaultExpandAll
            />
          </Form.Item>
        </>
      )}
      {entity ? (
        <Form.Item name="principalCandidateIds" label="负责人">
          <TreeSelect
            allowClear
            multiple
            showSearch={{ treeNodeFilterProp: 'title' }}
            treeData={principalTreeData}
            treeNodeLabelProp="title"
          />
        </Form.Item>
      ) : (
        <ProFormSelect
          name="memberIds"
          label="负责人"
          options={memberOptions}
          rules={[{ required: true, type: 'array', min: 1, message: '请至少选择一位负责人' }]}
          fieldProps={{ mode: 'multiple', optionFilterProp: 'label', showSearch: true }}
        />
      )}
    </ModalForm>
  );
};

OperationEntityForm.propTypes = {
  entities: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      enabled: PropTypes.bool.isRequired,
      id: PropTypes.string.isRequired,
      isStore: PropTypes.bool,
      name: PropTypes.string.isRequired,
      parentId: PropTypes.string,
    }),
  ).isRequired,
  entity: PropTypes.shape({
    code: PropTypes.string.isRequired,
    enabled: PropTypes.bool.isRequired,
    id: PropTypes.string.isRequired,
    isStore: PropTypes.bool,
    name: PropTypes.string.isRequired,
    parentId: PropTypes.string,
    principals: PropTypes.arrayOf(
      PropTypes.shape({
        externalUserId: PropTypes.string,
        id: PropTypes.string,
        memberId: PropTypes.string,
      }),
    ),
  }),
  memberOptions: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string.isRequired, value: PropTypes.string.isRequired }),
  ).isRequired,
  onFinish: PropTypes.func.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  principalContext: PropTypes.shape({
    collectionId: PropTypes.string,
    contactTree: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    ),
    recommendedContact: PropTypes.shape({
      externalUserId: PropTypes.string,
    }),
  }),
};

export default OperationEntityForm;
